import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import { Suspense, useEffect, useMemo, useRef, useState } from 'react'
import * as THREE from 'three'
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib'
import GraphScene from './components/GraphScene'
import StarField from './components/StarField'
import SidePanel from './components/SidePanel'
import SearchBar from './components/SearchBar'
import FilterControls from './components/FilterControls'
import {
  API_BASE,
  NoteNode,
  Edge,
  NoteDetail,
  Connection,
  Vec3,
  TYPE_ORDER,
  normalizeType,
} from './components/types'
import './App.css'

interface FocusTarget {
  pos: Vec3
  nonce: number
}

// Rig que anima la cámara hacia un planeta (doble click → centrar)
function CameraRig({
  focus,
  controlsRef,
}: {
  focus: FocusTarget | null
  controlsRef: React.MutableRefObject<OrbitControlsImpl | null>
}) {
  const { camera } = useThree()
  const targetPos = useRef<THREE.Vector3 | null>(null)
  const targetLook = useRef<THREE.Vector3 | null>(null)

  useEffect(() => {
    if (!focus) return
    const look = new THREE.Vector3(focus.pos[0], focus.pos[1], focus.pos[2])
    const dir = camera.position.clone().sub(look).normalize()
    if (dir.lengthSq() === 0) dir.set(0, 0, 1)
    targetPos.current = look.clone().add(dir.multiplyScalar(9))
    targetLook.current = look
  }, [focus, camera])

  useFrame(() => {
    const controls = controlsRef.current
    if (!targetPos.current || !targetLook.current || !controls) return
    camera.position.lerp(targetPos.current, 0.08)
    controls.target.lerp(targetLook.current, 0.08)
    controls.update()
    if (camera.position.distanceTo(targetPos.current) < 0.06) {
      targetPos.current = null
      targetLook.current = null
    }
  })

  return null
}

function App() {
  const [nodes, setNodes] = useState<NoteNode[]>([])
  const [edges, setEdges] = useState<Edge[]>([])
  const [stats, setStats] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<NoteDetail | null>(null)
  const [connections, setConnections] = useState<Connection[]>([])
  const [detailLoading, setDetailLoading] = useState(false)

  const [highlightedIds, setHighlightedIds] = useState<Set<string> | null>(null)
  const [activeTypes, setActiveTypes] = useState<Set<string>>(
    () => new Set(TYPE_ORDER),
  )
  const [focus, setFocus] = useState<FocusTarget | null>(null)

  const controlsRef = useRef<OrbitControlsImpl | null>(null)

  // Fetch inicial del grafo + stats
  useEffect(() => {
    async function fetchData() {
      try {
        const [graphRes, statsRes] = await Promise.all([
          fetch(`${API_BASE}/api/graph`),
          fetch(`${API_BASE}/api/stats`),
        ])
        const graph = await graphRes.json()
        const statsData = await statsRes.json()
        setNodes(graph.nodes || [])
        setEdges(graph.edges || [])
        setStats(statsData.stats)
      } catch (err) {
        console.error('No se pudieron cargar los datos:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
    const interval = setInterval(fetchData, 30000)
    return () => clearInterval(interval)
  }, [])

  // Mapa id → nodo para lookups en panel/búsqueda
  const nodeMap = useMemo(() => {
    const m = new Map<string, NoteNode>()
    nodes.forEach((n) => m.set(n.id, n))
    return m
  }, [nodes])

  // Conteos por tipo para los filtros
  const typeCounts = useMemo(() => {
    const c: Record<string, number> = {}
    nodes.forEach((n) => {
      const t = normalizeType(n.note_type)
      c[t] = (c[t] || 0) + 1
    })
    return c
  }, [nodes])

  // Cargar detalle al seleccionar
  useEffect(() => {
    if (!selectedId) {
      setDetail(null)
      setConnections([])
      return
    }
    let cancelled = false
    setDetailLoading(true)
    fetch(`${API_BASE}/api/notes/${selectedId}`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return
        setDetail(data.note || null)
        setConnections(data.connections || [])
      })
      .catch((err) => {
        if (cancelled) return
        console.error('No se pudo cargar la nota:', err)
        setDetail(null)
        setConnections([])
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedId])

  const handleSelect = (node: NoteNode) => setSelectedId(node.id)

  const handleFocus = (_node: NoteNode, position: Vec3) => {
    setFocus({ pos: position, nonce: Date.now() })
  }

  const handleSelectById = (id: string) => setSelectedId(id)

  const toggleType = (type: string) => {
    setActiveTypes((prev) => {
      const next = new Set(prev)
      if (next.has(type)) next.delete(type)
      else next.add(type)
      return next
    })
  }

  return (
    <div className="app">
      <header className="header">
        <h1>🌌 Knowledge Universe</h1>
        {stats && (
          <div className="stats">
            <span>Notas: {stats.total_notes}</span>
            <span>Conexiones: {stats.total_edges}</span>
            <span>Media: {stats.avg_connections}</span>
          </div>
        )}
      </header>

      <SearchBar onResults={setHighlightedIds} onSelect={handleSelectById} />

      <FilterControls
        activeTypes={activeTypes}
        counts={typeCounts}
        onToggle={toggleType}
      />

      <Canvas
        camera={{ position: [0, 0, 40], fov: 70 }}
        onPointerMissed={() => setSelectedId(null)}
      >
        <Suspense fallback={null}>
          <color attach="background" args={['#05050a']} />
          <fog attach="fog" args={['#05050a', 60, 200]} />
          <ambientLight intensity={0.2} />
          <directionalLight position={[20, 30, 20]} intensity={1.5} />
          <directionalLight position={[-20, -10, -20]} color="#4488ff" intensity={0.5} />
          <StarField />
          <GraphScene
            nodes={nodes}
            edges={edges}
            selectedId={selectedId}
            highlightedIds={highlightedIds}
            activeTypes={activeTypes}
            onSelect={handleSelect}
            onFocus={handleFocus}
          />
          <CameraRig focus={focus} controlsRef={controlsRef} />
          <OrbitControls
            ref={controlsRef}
            enableDamping
            dampingFactor={0.05}
            maxDistance={160}
            minDistance={4}
          />
        </Suspense>
      </Canvas>

      <SidePanel
        detail={detail}
        connections={connections}
        loading={detailLoading}
        selectedId={selectedId}
        nodeMap={nodeMap}
        onClose={() => setSelectedId(null)}
        onSelectConnection={handleSelectById}
      />

      {loading && <div className="loading">Cargando grafo de conocimiento…</div>}
    </div>
  )
}

export default App
